import { ReactNode, Ref, SyntheticEvent, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { InfoTooltip, Props as InfoTooltipProps } from '@common/components/InfoTooltip';
import { ChevronDownIcon } from '@/assets/icons';
import { useAccordionExpanded } from '@/modules/userPreferences/hooks';
import { AccordionPreferenceName } from '@/modules/userPreferences/hooks/useAccordionExpanded';

type Props = {
  children: ReactNode;
  title?: ReactNode;
  titleKey?: string;
  titleSx?: SxProps;
  InfoTooltipProps?: Omit<InfoTooltipProps, 'children'>;
  sx?: SxProps;
  onChange?: (expanded: boolean) => void;
  fontSize?: string;
  beforeIcon?: ReactNode;
  afterIcon?: ReactNode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  unmountOnExit?: boolean;
  expandIcon?: ReactNode;
  /** When set, the open/closed state is remembered in this user preference (its default applies) */
  persistId?: AccordionPreferenceName;
};
// plz fix me
// eslint-disable-next-line react/display-name
export const Accordion = forwardRef(
  (
    {
      children,
      sx,
      title,
      titleKey,
      InfoTooltipProps,
      onChange,
      titleSx,
      fontSize = '14px',
      afterIcon,
      beforeIcon,
      expanded,
      defaultExpanded,
      className,
      unmountOnExit = false,
      expandIcon = <ChevronDownIcon />,
      persistId,
    }: Props,
    ref: Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation();
    
    const summary = (
      <Stack
        direction='row'
        justifyContent='left'
        gap='12px'
        alignItems='center'
        width='100%'
      >
        {beforeIcon}
        <Typography sx={titleSx} component='span'>
          {titleKey ? t(titleKey) : title}
        </Typography>
      </Stack>
    );

    const { expanded: persistedExpanded, setExpanded: setPersistedExpanded } =
      useAccordionExpanded(persistId);

    const onInnerChange = useCallback(
      (e: SyntheticEvent, isExpanded: boolean) => {
        setPersistedExpanded(isExpanded);
        onChange?.(isExpanded);
      },
      [onChange, setPersistedExpanded],
    );

    return (
      <MuiAccordion
        ref={ref}
        sx={sx}
        defaultExpanded={defaultExpanded}
        // A parent-controlled `expanded` wins; otherwise use the remembered state (if persistId is set)
        expanded={expanded ?? persistedExpanded}
        onChange={onInnerChange}
        className={className}
        TransitionProps={{ unmountOnExit }}
      >
        <Stack position='relative'>
          <AccordionSummary
            expandIcon={expandIcon}
            sx={{
              '& .MuiTypography-root': { fontSize },
              '& .MuiAccordionSummary-content': {
                paddingRight: afterIcon ? '24px' : undefined,
              },
              minWidth: '0px',
            }}
          >
            {InfoTooltipProps ? <InfoTooltip {...InfoTooltipProps}>{summary}</InfoTooltip> : summary}
          </AccordionSummary>
          {afterIcon && (
            <Stack
              position='absolute'
              right={expandIcon ? '28px' : '8px'}
              top={0}
              height='100%'
              width='32px'
              alignItems='center'
              justifyContent='center'
            >
              {afterIcon}
            </Stack>
          )}
        </Stack>
        <AccordionDetails>{children}</AccordionDetails>
      </MuiAccordion>
    );
  },
);
